import { useFormik } from "formik";
import * as Yup from "yup";
import { V3ModalBase } from "../V3ModalBase";
import { VerticalSpacing } from "../VerticalSpacing";
import { RightAlignPanel, V3BodyGrey35 } from "../ReusableStyledComponents";
import { InputWithText } from "../V3ModalUpdateSlug/V3ModalUpdateSlug.styles";
import { V3TextInput } from "../TextInput/V3TextInput";
import { V3Button, V3ButtonColor } from "../Button";
import { SmartShareLinkResponse, SmartShareType } from "@authory-packages/core";
import { useRouter } from "next/router";
import { APPLICATION_ROUTES } from "../../shared-logic";
import { InlineLink } from "../ReusableStyledComponents/InlineLink";

const SLUG_REGEX = /^[\w-]+$/;

export type CreateSmartShareFormik = {
    name: string;
    type: SmartShareType;
};

interface V3ModalCreateSmartShareLinkProps {
    modalOpen: boolean;
    onCloseHandler: () => void;
    onCreateSmartShareLinkHandler: (data: CreateSmartShareFormik) => Promise<SmartShareLinkResponse | undefined>;
    baseUrl: string;
    type: SmartShareType;
}

export const V3ModalCreateSmartShareLink = ({
    modalOpen,
    baseUrl,
    type,
    onCloseHandler,
    onCreateSmartShareLinkHandler
}: V3ModalCreateSmartShareLinkProps) => {
    const router = useRouter();
    const formik = useFormik<CreateSmartShareFormik>({
        enableReinitialize: true,
        initialValues: {
            name: "",
            type
        },
        onSubmit: async (data) => {
            try {
                const res = await onCreateSmartShareLinkHandler(data);
                if (res?.uid) {
                    router.push(`${APPLICATION_ROUTES.SETTINGS_SMART_SHARE_LINK.replace("[linkId]", res.uid)}`);
                }
                onCloseHandler();
            } catch {}
        },
        validationSchema: Yup.object().shape({
            name: Yup.string()
                .required("Please enter a link name")
                .matches(SLUG_REGEX, { message: "Use only letters and numbers (no spaces or special characters)" })
        })
    });

    return (
        <V3ModalBase isOpen={modalOpen} onClose={onCloseHandler} title={"Create Smart Share Link"}>
            <VerticalSpacing bottom={18}>
                <V3BodyGrey35>
                    When you share your work for a job application or a pitch, you usually have no way of knowing if it was ever opened.
                </V3BodyGrey35>
            </VerticalSpacing>
            <VerticalSpacing bottom={18}>
                <V3BodyGrey35>
                    A Smart Share Link changes that: You'll see exactly when your link was viewed, from where, and which of your work samples were
                    clicked.
                </V3BodyGrey35>
            </VerticalSpacing>
            <VerticalSpacing bottom={18}>
                <V3BodyGrey35>
                    You can create as many Smart Share Links as you want for the same item. Each one tracks separately, so create a different link for
                    each person you share with.
                </V3BodyGrey35>
            </VerticalSpacing>
            <VerticalSpacing bottom={28}>
                <V3BodyGrey35>
                    Give this link a name so you can tell your links apart later.{" "}
                    <InlineLink href="https://authory.com/help/en/articles/14300805-how-smart-share-links-work-in-authory" target="_blank">
                        Learn more
                    </InlineLink>
                </V3BodyGrey35>
            </VerticalSpacing>
            <form onSubmit={formik.handleSubmit}>
                <InputWithText>
                    <div>
                        <V3BodyGrey35>{baseUrl.replace("https://", "").replace("http://", "")}</V3BodyGrey35>
                    </div>
                    <div>
                        <V3TextInput
                            name="name"
                            placeholder="e.g. nytimes-application"
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={formik.values.name}
                            error={formik.errors.hasOwnProperty("name") && formik.touched.hasOwnProperty("name") ? formik.errors.name : undefined}
                        />
                    </div>
                </InputWithText>
                <VerticalSpacing top={24}>
                    <RightAlignPanel>
                        <div>
                            <V3Button text="Cancel" type="button" color={V3ButtonColor.secondary} onClick={onCloseHandler} />
                        </div>
                        <div>
                            <V3Button text="Create Link" type="submit" />
                        </div>
                    </RightAlignPanel>
                </VerticalSpacing>
            </form>
        </V3ModalBase>
    );
};
